import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';

const BOOTH_URL = '#booth'; // TODO: Boothページ公開後に更新
const BASIC_PRICE = '¥1,000';
const PRO_PRICE   = '¥2,000';
const TRIAL_FORM_URL = 'https://forms.gle/aV4sztszngJNUaAw7';
const CONTACT_URL = 'https://x.com/rio_engineer?s=20';
const MAX_CASTS = 5;
const MAX_GUESTS = 6;

// ── CSS keyframes injection ──────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    html { scroll-behavior: smooth; }

    @keyframes float {
      0%,100% { transform: translateY(0px) rotate(0deg); }
      33%      { transform: translateY(-10px) rotate(0.4deg); }
      66%      { transform: translateY(-5px) rotate(-0.4deg); }
    }
    @keyframes gradientFlow {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes glowPulse {
      0%,100% { box-shadow: 0 0 24px rgba(108,99,255,0.25); }
      50%      { box-shadow: 0 0 48px rgba(108,99,255,0.55), 0 0 80px rgba(108,99,255,0.15); }
    }
    @keyframes bgDrift {
      0%,100% { background-position: 0% 0%; }
      50%      { background-position: 100% 100%; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.92); }
      to   { opacity: 1; transform: scale(1); }
    }
    @keyframes slideRight {
      from { opacity: 0; transform: translateX(-20px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes badge-pop {
      0%   { transform: scale(0.8); opacity: 0; }
      60%  { transform: scale(1.08); }
      100% { transform: scale(1);    opacity: 1; }
    }
    @keyframes dotPulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(108,99,255,0.45), 0 0 0 5px #f5f3ff; }
      50%      { box-shadow: 0 0 0 9px rgba(108,99,255,0), 0 0 0 5px #f5f3ff; }
    }
    /* カードが定着するときの「ピコん」ポップ */
    @keyframes cardPop {
      0%   { transform: translateY(0) scale(1); }
      30%  { transform: translateY(0) scale(1.045); }
      62%  { transform: translateY(0) scale(0.99); }
      100% { transform: translateY(0) scale(1); }
    }
    /* 枝が伸びている間、対象カードの枠線を「ピコんピコん」 */
    @keyframes cardBorderPulse {
      0%,100% { box-shadow: 0 0 0 0 var(--lanePulse,#6c63ff99), 0 4px 14px rgba(0,0,0,0.05); }
      50%      { box-shadow: 0 0 0 6px var(--lanePulse,#6c63ff99), 0 4px 14px rgba(0,0,0,0.05); }
    }
    /* ── Use-case flow (swimlane) ── */
    .lb-uc-lanes-head {
      display: grid; grid-template-columns: 56px repeat(3, 1fr); gap: 0 16px;
      margin-bottom: 8px;
    }
    .lb-uc-row {
      display: grid; grid-template-columns: 56px repeat(3, 1fr); gap: 0 16px;
      align-items: center; min-height: 50px; margin-bottom: 13px;
    }
    .lb-uc-row:last-child { margin-bottom: 0; }
    .lb-uc-actor-chip { display: none; }

    @media (max-width: 720px) {
      .lb-uc-lanes-head { display: none; }
      .lb-uc-row        { grid-template-columns: 44px 1fr; gap: 0 12px; min-height: 0; margin-bottom: 14px; align-items: stretch; }
      .lb-uc-card       { grid-column: 2 !important; }
      .lb-uc-actor-chip { display: inline-flex !important; }
    }

    .lb-hero-title-accent {
      background: linear-gradient(270deg, #6c63ff, #ec4899, #38bdf8, #6c63ff);
      background-size: 300% 300%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: gradientFlow 5s ease infinite;
    }
    .lb-hero-card {
      animation: float 5s ease-in-out infinite;
    }
    .lb-cta-banner {
      background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #0f1f3d 100%);
      background-size: 300% 300%;
      animation: bgDrift 10s ease infinite;
    }
    .lb-primary-btn {
      transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
    }
    .lb-primary-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 14px 32px rgba(108,99,255,0.52) !important;
    }
    .lb-secondary-btn {
      transition: transform 0.18s, background 0.18s;
    }
    .lb-secondary-btn:hover { transform: translateY(-2px); }
    .lb-nav-link { transition: color 0.15s; }
    .lb-nav-link:hover { color: #6c63ff !important; }
    .lb-feature-card {
      transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
    }
    .lb-feature-card:hover {
      transform: translateY(-6px) !important;
      box-shadow: 0 20px 48px rgba(108,99,255,0.16) !important;
      border-color: #6c63ff55 !important;
    }
    .lb-plan-card {
      transition: transform 0.25s, box-shadow 0.25s;
    }
    .lb-footer-link { transition: color 0.15s; }
    .lb-footer-link:hover { color: #a5b4fc !important; }

    .lb-add-input {
      width: 100%;
      padding: 7px 10px;
      border-radius: 7px;
      border: 1.5px solid #6c63ff66;
      font-size: 12px;
      outline: none;
      box-sizing: border-box;
      background: #f8f9fd;
      color: #1e293b;
      margin-top: 6px;
      animation: fadeIn 0.18s ease;
    }
    .lb-add-input:focus { border-color: #6c63ff; }
    .lb-result-row { animation: fadeUp 0.3s ease both; }

    /* ── Responsive ── */
    .lb-nav-links   { display: flex; align-items: center; gap: 28px; }
    .lb-hero        { display: flex; align-items: center; gap: 48px; max-width: 1100px; margin: 0 auto; padding: 72px 24px 64px; position: relative; }
    .lb-hero-right  { flex-shrink: 0; position: relative; z-index: 1; }
    .lb-feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .lb-demo-wrap   { display: flex; height: 440px; border-radius: 16px; overflow: hidden; box-shadow: 0 32px 80px rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.06); }
    .lb-demo-sidebar { width: 204px; background: #0c1424; display: flex; flex-direction: column; padding: 16px 12px; flex-shrink: 0; }
    .lb-plan-grid   { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .lb-cta-btns    { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }

    @media (max-width: 900px) {
      .lb-feature-grid { grid-template-columns: repeat(2, 1fr); }
      .lb-plan-grid    { grid-template-columns: repeat(2, 1fr) !important; }
    }

    @media (max-width: 768px) {
      .lb-hero         { flex-direction: column; padding: 48px 20px 40px; gap: 32px; }
      .lb-hero-right   { display: none; }
    }

    @media (max-width: 640px) {
      .lb-nav-links        { display: none; }
      .lb-nav-mobile-cta   { display: block !important; }
      .lb-feature-grid     { grid-template-columns: 1fr; }
      .lb-plan-grid        { grid-template-columns: 1fr !important; }
      .lb-demo-sidebar     { display: none; }
      .lb-demo-wrap        { height: auto; min-height: 360px; }
      .lb-cta-btns         { flex-direction: column; align-items: stretch; }
    }

    @media (max-width: 768px) {
      .lb-hero-title-size  { font-size: 34px !important; }
      .lb-hero-desc-size   { font-size: 15px !important; }
      .lb-section-title    { font-size: 26px !important; }
      .lb-section-pad      { padding: 56px 20px !important; }
      .lb-cta-secondary    { text-align: center; }
    }
  `}</style>
);

// **text** を <strong> に変換するヘルパー
const renderBold = (text) => {
  const parts = text.split(/\*\*(.*?)\*\*/);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ fontWeight: 800, color: '#1e293b' }}>{part}</strong>
      : part
  );
};

// ── Scroll-triggered animation hook ──────────────────────────────────────────
const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

// ── スクロール連動の進行度フック（0〜1） ──────────────────────────────────────
// 縦に長いセクションを sticky で固定し、スクロール量に応じて progress が進む。
// モバイルでは固定演出を無効化し progress=1（全ステップ表示）にする。
const useScrollyProgress = () => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const update = () => {
      const mobile = window.innerWidth <= 720;
      setIsMobile(mobile);
      const el = ref.current;
      if (!el) return;
      if (mobile) { setProgress(1); return; }
      const rect = el.getBoundingClientRect();
      const scrollLen = rect.height - window.innerHeight;
      const p = scrollLen > 0 ? Math.min(1, Math.max(0, -rect.top / scrollLen)) : 0;
      setProgress(p);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return [ref, progress, isMobile];
};

// ── ユースケースのレーン定義・ステップ定義 ───────────────────────────────────
const UC_LANES = {
  owner: { label: '運営',     color: '#6c63ff', col: 2 },
  cast:  { label: 'キャスト', color: '#0ea5e9', col: 3 },
  guest: { label: 'ゲスト',   color: '#ec4899', col: 4 },
};

const UC_STEPS = [
  { lane: 'owner', icon: '📋', title: '応募フォームを作成・公開', note: 'URLをXでシェアするだけ',           phase: '開催前' },
  { lane: 'guest', icon: '📝', title: 'フォームから応募',         note: 'VRChat名・希望を入力',             from: 'owner' },
  { lane: 'cast',  icon: '📅', title: '出欠リンクで出勤登録',     note: 'スマホからワンタップで回答' },
  { lane: 'owner', icon: '📊', title: '応募・出勤を自動集計',     note: 'システムにリアルタイム反映',       from: 'guest' },
  { lane: 'owner', icon: '🎲', title: 'ワンクリックで抽選',       note: '希望・優先度・部制を最適化',       phase: '抽選日' },
  { lane: 'guest', icon: '🏠', title: 'ポータルで当選を確認',     note: '結果をいつでもチェック',           from: 'owner' },
  { lane: 'owner', icon: '📬', title: '当選者の連絡先へ即アクセス', note: 'X / Discord にそのまま連絡',     from: 'guest' },
  { lane: 'all',   icon: '🎉', title: 'イベント開催！',           note: '運営・キャスト・ゲストが集合',     phase: '開催日' },
];

// ── Interactive Demo ──────────────────────────────────────────────────────────
const CAST_COLORS = ['#6c63ff', '#8b5cf6', '#a78bfa', '#7c3aed', '#6d28d9'];
const GUEST_COLORS = ['#0ea5e9', '#06b6d4', '#3b82f6', '#14b8a6', '#0284c7', '#2563eb'];

const DemoPreview = () => {
  const [selectedEvent, setSelectedEvent] = useState('event1');
  const [activeTab, setActiveTab] = useState('manage');

  const [casts, setCasts] = useState([
    { id: 'c1', name: 'キャストX', capacity: 1 },
    { id: 'c2', name: 'キャストY', capacity: 2 },
    { id: 'c3', name: 'キャストZ', capacity: 1 },
  ]);
  const [guests, setGuests] = useState([
    { id: 'g1', name: 'ゲストA' },
    { id: 'g2', name: 'ゲストB' },
    { id: 'g3', name: 'ゲストC' },
  ]);

  const [addingCast, setAddingCast]   = useState(false);
  const [addingGuest, setAddingGuest] = useState(false);
  const [newCastName, setNewCastName]   = useState('');
  const [newGuestName, setNewGuestName] = useState('');

  const [matchResult, setMatchResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const demoEvents = [
    { id: 'event1', name: '第1回マッチングイベント', mode: '両想い' },
    { id: 'event2', name: 'VRC交流会 春', mode: 'キャスト' },
  ];

  const handleAddCast = () => {
    if (!newCastName.trim() || casts.length >= MAX_CASTS) return;
    setCasts(p => [...p, { id: `c${Date.now()}`, name: newCastName.trim(), capacity: 1 }]);
    setNewCastName(''); setAddingCast(false); setMatchResult(null);
  };
  const handleAddGuest = () => {
    if (!newGuestName.trim() || guests.length >= MAX_GUESTS) return;
    setGuests(p => [...p, { id: `g${Date.now()}`, name: newGuestName.trim() }]);
    setNewGuestName(''); setAddingGuest(false); setMatchResult(null);
  };

  const runMatching = () => {
    setIsRunning(true);
    setTimeout(() => {
      const results = casts.map((c, i) => {
        const g = guests[i % guests.length];
        return {
          cast: c.name,
          guest: g ? g.name : '未マッチ',
          type: i < Math.min(2, guests.length) ? '両想い ❤️' : g ? 'キャスト希望' : '—',
          room: g ? `Room ${String.fromCharCode(65 + i)}` : '—',
        };
      });
      setMatchResult(results);
      setIsRunning(false);
      setActiveTab('matching');
    }, 1300);
  };

  return (
    <div className="lb-demo-wrap" style={dm.wrap}>
      {/* Sidebar */}
      <div className="lb-demo-sidebar" style={dm.sidebar}>
        <div style={dm.logo}>
          <img src="/logo.png" alt="" style={{ height: 22, width: 'auto' }} />
          <span style={dm.logoText}>LinkBoard</span>
          <span style={dm.demoTag}>DEMO</span>
        </div>
        <div style={dm.navItem}>📋 イベント管理</div>
        <div style={dm.eventsLabel}>EVENTS</div>
        {demoEvents.map(ev => (
          <button
            key={ev.id}
            onClick={() => { setSelectedEvent(ev.id); setMatchResult(null); setActiveTab('manage'); }}
            style={{
              ...dm.eventBtn,
              background:   selectedEvent === ev.id ? 'rgba(108,99,255,0.15)' : 'transparent',
              borderLeft:   selectedEvent === ev.id ? '3px solid #6c63ff' : '3px solid transparent',
              color:        selectedEvent === ev.id ? '#a5b4fc' : '#64748b',
            }}
          >
            <span style={dm.eventBtnName}>{ev.name}</span>
            <span style={dm.eventBtnMode}>{ev.mode}</span>
          </button>
        ))}
        <div style={dm.sidebarFooter}>
          <div style={dm.avatar}>U</div>
          <span style={{ fontSize: 10, color: '#475569' }}>demo@LinkBoard.app</span>
        </div>
      </div>

      {/* Main */}
      <div style={dm.main}>
        {/* Tab bar */}
        <div style={dm.tabBar}>
          {[
            { id: 'manage',   label: '⚙️ イベント管理' },
            { id: 'matching', label: '🎲 抽選実行' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              ...dm.tab,
              color:        activeTab === t.id ? '#6c63ff' : '#94a3b8',
              fontWeight:   activeTab === t.id ? 800 : 600,
              borderBottom: activeTab === t.id ? '2px solid #6c63ff' : '2px solid transparent',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={dm.content}>

          {/* ── Manage tab ── */}
          {activeTab === 'manage' && (
            <div style={dm.manageGrid}>
              {/* Cast panel */}
              <div style={dm.panel}>
                <div style={dm.panelHeader}>
                  <span style={dm.panelTitle}>🎭 キャスト</span>
                  <span style={dm.countBadge}>{casts.length}/{MAX_CASTS}</span>
                </div>
                <div style={dm.listWrap}>
                  {casts.map((c, i) => (
                    <div key={c.id} style={dm.personRow}>
                      <div style={{ ...dm.personAvatar, background: CAST_COLORS[i % CAST_COLORS.length] }}>
                        {c.name[0]}
                      </div>
                      <div>
                        <div style={dm.personName}>{c.name}</div>
                        <div style={dm.personSub}>定員 {c.capacity}名</div>
                      </div>
                    </div>
                  ))}
                </div>
                {addingCast ? (
                  <div>
                    <input
                      className="lb-add-input"
                      autoFocus
                      placeholder="キャスト名を入力…"
                      value={newCastName}
                      onChange={e => setNewCastName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddCast(); if (e.key === 'Escape') { setAddingCast(false); setNewCastName(''); } }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button onClick={handleAddCast} style={dm.addConfirmBtn} disabled={!newCastName.trim()}>追加</button>
                      <button onClick={() => { setAddingCast(false); setNewCastName(''); }} style={dm.addCancelBtn}>✕</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => casts.length < MAX_CASTS && setAddingCast(true)}
                    style={{ ...dm.addBtn, opacity: casts.length >= MAX_CASTS ? 0.4 : 1, cursor: casts.length >= MAX_CASTS ? 'not-allowed' : 'pointer' }}
                  >
                    {casts.length >= MAX_CASTS ? `上限 ${MAX_CASTS}名` : '+ 追加'}
                  </button>
                )}
              </div>

              {/* Guest panel */}
              <div style={dm.panel}>
                <div style={dm.panelHeader}>
                  <span style={dm.panelTitle}>🙋 ゲスト</span>
                  <span style={{ ...dm.countBadge, color: '#0ea5e9', background: 'rgba(14,165,233,0.12)' }}>{guests.length}/{MAX_GUESTS}</span>
                </div>
                <div style={dm.listWrap}>
                  {guests.map((g, i) => (
                    <div key={g.id} style={dm.personRow}>
                      <div style={{ ...dm.personAvatar, background: GUEST_COLORS[i % GUEST_COLORS.length] }}>
                        {g.name[0]}
                      </div>
                      <div>
                        <div style={dm.personName}>{g.name}</div>
                        <div style={dm.personSub}>参加登録済み</div>
                      </div>
                    </div>
                  ))}
                </div>
                {addingGuest ? (
                  <div>
                    <input
                      className="lb-add-input"
                      autoFocus
                      placeholder="ゲスト名を入力…"
                      value={newGuestName}
                      onChange={e => setNewGuestName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddGuest(); if (e.key === 'Escape') { setAddingGuest(false); setNewGuestName(''); } }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button onClick={handleAddGuest} style={{ ...dm.addConfirmBtn, background: '#0ea5e9' }} disabled={!newGuestName.trim()}>追加</button>
                      <button onClick={() => { setAddingGuest(false); setNewGuestName(''); }} style={dm.addCancelBtn}>✕</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => guests.length < MAX_GUESTS && setAddingGuest(true)}
                    style={{ ...dm.addBtn, opacity: guests.length >= MAX_GUESTS ? 0.4 : 1, cursor: guests.length >= MAX_GUESTS ? 'not-allowed' : 'pointer' }}
                  >
                    {guests.length >= MAX_GUESTS ? `上限 ${MAX_GUESTS}名` : '+ 追加'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Matching tab ── */}
          {activeTab === 'matching' && (
            <div style={{ padding: '20px 24px' }}>
              {!matchResult ? (
                <div style={dm.matchReady}>
                  <div style={dm.matchIcon}>🎲</div>
                  <h3 style={dm.matchTitle}>抽選を実行する</h3>
                  <p style={dm.matchSub}>
                    キャスト {casts.length}名 / ゲスト {guests.length}名<br />
                    希望リストと優先度をもとに自動マッチング＆部屋割りを生成します
                  </p>
                  <button
                    className="lb-primary-btn"
                    onClick={runMatching}
                    style={{ ...dm.runBtn, opacity: isRunning ? 0.7 : 1 }}
                    disabled={isRunning}
                  >
                    {isRunning ? '⏳ 計算中...' : '🚀 抽選スタート'}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={dm.resultHeader}>
                    <span style={dm.resultTitle}>✅ マッチング結果 &amp; 部屋割り</span>
                    <button onClick={() => { setMatchResult(null); }} style={dm.resetBtn}>やり直す</button>
                  </div>
                  <div style={dm.resultList}>
                    {matchResult.map((r, i) => (
                      <div key={i} className="lb-result-row" style={{ ...dm.resultRow, animationDelay: `${i * 0.08}s` }}>
                        <div style={dm.resultCast}>{r.cast}</div>
                        <div style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }}>→</div>
                        <div style={dm.resultGuest}>{r.guest}</div>
                        <div style={{ ...dm.resultRoom, opacity: r.room === '—' ? 0.35 : 1 }}>{r.room}</div>
                        <div style={{ ...dm.resultType, color: r.type.includes('両想い') ? '#10b981' : r.type === '—' ? '#94a3b8' : '#f59e0b' }}>{r.type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const dm = {
  wrap: { borderRadius: 16, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.06)' },
  sidebar: { width: 204, background: '#0c1424', flexDirection: 'column', padding: '16px 12px', flexShrink: 0 },
  logo: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 18px', flexShrink: 0 },
  logoText: { fontSize: 13, fontWeight: 800, color: '#fff', flex: 1 },
  demoTag: { fontSize: 9, fontWeight: 800, color: '#6c63ff', background: 'rgba(108,99,255,0.2)', padding: '2px 6px', borderRadius: 4, letterSpacing: 1 },
  navItem: { fontSize: 12, fontWeight: 700, color: '#fff', background: '#6c63ff', padding: '8px 10px', borderRadius: 8, marginBottom: 14 },
  eventsLabel: { fontSize: 9, fontWeight: 700, color: '#334155', letterSpacing: 2, marginBottom: 6, paddingLeft: 8 },
  eventBtn: { display: 'flex', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', padding: '7px 10px', borderRadius: '0 6px 6px 0', marginBottom: 2, transition: 'all 0.15s' },
  eventBtnName: { fontSize: 11, fontWeight: 600 },
  eventBtnMode: { fontSize: 9, color: '#475569', fontWeight: 400 },
  sidebarFooter: { marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' },
  avatar: { width: 22, height: 22, borderRadius: 6, background: '#6c63ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 },
  main: { flex: 1, background: '#f8f9fd', display: 'flex', flexDirection: 'column', minWidth: 0 },
  tabBar: { display: 'flex', borderBottom: '1px solid #e8edf5', padding: '0 20px', background: '#fff', flexShrink: 0 },
  tab: { padding: '11px 14px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, transition: 'all 0.15s', whiteSpace: 'nowrap' },
  content: { flex: 1, overflow: 'auto' },
  manageGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, padding: 16 },
  panel: { background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: 6 },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  panelTitle: { fontSize: 12.5, fontWeight: 700, color: '#1e293b' },
  countBadge: { fontSize: 10, color: '#6c63ff', fontWeight: 700, background: 'rgba(108,99,255,0.1)', padding: '2px 8px', borderRadius: 20 },
  listWrap: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  personRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', background: '#f8f9fd', borderRadius: 8, animation: 'fadeIn 0.2s ease' },
  personAvatar: { width: 26, height: 26, borderRadius: 7, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 },
  personName: { fontSize: 11, fontWeight: 700, color: '#1e293b' },
  personSub: { fontSize: 9.5, color: '#94a3b8', marginTop: 1 },
  addBtn: { marginTop: 4, background: 'transparent', border: '1.5px dashed #e2e8f0', borderRadius: 8, padding: '7px', fontSize: 11, color: '#94a3b8', fontWeight: 600, textAlign: 'center', transition: 'border-color 0.15s, color 0.15s' },
  addConfirmBtn: { flex: 1, background: '#6c63ff', color: '#fff', border: 'none', borderRadius: 7, padding: '6px', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  addCancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 7, padding: '6px 10px', fontSize: 11, cursor: 'pointer' },
  matchReady: { textAlign: 'center', paddingTop: 30 },
  matchIcon: { fontSize: 44, marginBottom: 10 },
  matchTitle: { fontSize: 15, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' },
  matchSub: { fontSize: 12, color: '#64748b', lineHeight: 1.7, margin: '0 0 20px' },
  runBtn: { background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(108,99,255,0.42)' },
  resultHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  resultTitle: { fontSize: 13, fontWeight: 800, color: '#1e293b' },
  resetBtn: { fontSize: 11, color: '#6c63ff', background: 'transparent', border: '1px solid rgba(108,99,255,0.3)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 },
  resultList: { display: 'flex', flexDirection: 'column', gap: 7 },
  resultRow: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '10px 14px', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', fontSize: 12 },
  resultCast: { fontWeight: 700, color: '#1e293b', minWidth: 72, whiteSpace: 'nowrap' },
  resultGuest: { fontWeight: 700, color: '#1e293b', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  resultRoom: { fontSize: 10, fontWeight: 700, color: '#6c63ff', background: 'rgba(108,99,255,0.1)', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  resultType: { fontSize: 10.5, fontWeight: 700, minWidth: 72, textAlign: 'right', whiteSpace: 'nowrap' },
};

// ── Use-case Flow (スクロール連動スイムレーン) ───────────────────────────────
const stepColor = (step) => step.lane === 'all' ? '#8b5cf6' : UC_LANES[step.lane].color;

const UseCaseFlow = () => {
  const [wrapRef, progress, isMobile] = useScrollyProgress();
  const n = UC_STEPS.length;
  const activeIndex = Math.min(n - 1, Math.floor(progress * n));

  // カード同士をつなぐ線の座標をカードの実位置から測定する
  const laneRef  = useRef(null);
  const cardRefs = useRef([]);
  const [segs, setSegs] = useState([]);
  useEffect(() => {
    const measure = () => {
      const cont = laneRef.current;
      if (!cont || isMobile) { setSegs([]); return; }
      const cb = cont.getBoundingClientRect();
      const pts = cardRefs.current.map((el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const cx = r.left - cb.left + r.width / 2;
        return { top: { x: cx, y: r.top - cb.top }, bottom: { x: cx, y: r.bottom - cb.top } };
      });
      const list = [];
      for (let i = 0; i < pts.length - 1; i++) {
        if (!pts[i] || !pts[i + 1]) continue;
        list.push({ from: pts[i].bottom, to: pts[i + 1].top, color: stepColor(UC_STEPS[i + 1]) });
      }
      setSegs(list);
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [isMobile]);

  return (
    <section id="flow" style={uc.section(isMobile)}>
      <div ref={wrapRef} style={uc.scrollWrap(isMobile, n)}>
        <div style={uc.sticky(isMobile)}>
          <div style={s.sectionInner}>
            <div style={{ ...s.sectionTag, textAlign: 'center', marginBottom: 6 }}>HOW IT WORKS</div>
            <h2 className="lb-section-title" style={{ ...s.sectionTitle, textAlign: 'center', fontSize: 27, margin: '0 0 6px' }}>
              開催前から当日まで、3者が一気通貫
            </h2>
            <p style={{ ...s.sectionDesc, textAlign: 'center', fontSize: 15, marginBottom: 10 }}>
              運営・キャスト・ゲストの動きが、ひとつのシステムでつながります
            </p>

            {/* レーンヘッダー（デスクトップ） */}
            <div className="lb-uc-lanes-head">
              <div />
              {Object.values(UC_LANES).map((lane, i) => {
                const isActiveLane = UC_STEPS[activeIndex]?.lane === Object.keys(UC_LANES)[i] || UC_STEPS[activeIndex]?.lane === 'all';
                return (
                  <div key={lane.label} style={{
                    textAlign: 'center', fontSize: 13, fontWeight: 800,
                    padding: '8px 0', borderRadius: 10,
                    color: isActiveLane ? '#fff' : lane.color,
                    background: isActiveLane ? lane.color : `${lane.color}14`,
                    border: `1.5px solid ${lane.color}${isActiveLane ? '' : '33'}`,
                    transition: 'all 0.3s',
                  }}>
                    {lane.label}
                  </div>
                );
              })}
            </div>

            {/* スイムレーン本体 */}
            <div ref={laneRef} style={{ position: 'relative' }}>
              {/* カード同士をつなぐ成長線（デスクトップ） */}
              {!isMobile && segs.length > 0 && (
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, overflow: 'visible' }}>
                  {segs.map((sg, k) => {
                    const lp   = Math.max(0, Math.min(1, progress * n - (k + 1)));
                    const draw = Math.max(0, Math.min(1, lp / 0.45));
                    const grow = draw > 0 && draw < 1;
                    const midY = (sg.from.y + sg.to.y) / 2;
                    const d = `M ${sg.from.x} ${sg.from.y} C ${sg.from.x} ${midY}, ${sg.to.x} ${midY}, ${sg.to.x} ${sg.to.y}`;
                    return (
                      <path key={k} d={d} fill="none" stroke={sg.color} strokeWidth={3}
                        strokeLinecap="round" pathLength={1} strokeDasharray={1} strokeDashoffset={1 - draw}
                        style={{
                          transition: 'stroke-dashoffset 0.1s linear',
                          filter: grow ? `drop-shadow(0 0 4px ${sg.color}aa)` : 'none',
                        }} />
                    );
                  })}
                </svg>
              )}

              {UC_STEPS.map((step, i) => {
                const local   = Math.max(0, Math.min(1, progress * n - i));
                const active  = local > 0.04;       // カードが点灯（都度表示）
                const settled = local >= 0.45;      // 枝が届ききって定着
                const growing = active && !settled; // 枝が伸びている最中（＝今ここ）
                const lane = step.lane === 'all'
                  ? { label: 'みんな', color: '#8b5cf6', col: '2 / -1' }
                  : UC_LANES[step.lane];

                return (
                  <div key={i} className="lb-uc-row" style={{
                    position:   'relative', zIndex: 1,
                    opacity:    isMobile ? 1 : (active ? 1 : 0),
                    transition: 'opacity 0.35s ease',
                  }}>
                    {/* ノード（番号ドット） */}
                    <div style={{ gridColumn: '1 / 2', display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 900, color: '#fff',
                        background: active ? `linear-gradient(135deg, ${lane.color}, #8b5cf6)` : '#cbd5e1',
                        boxShadow: active ? '0 0 0 5px #f5f3ff, 0 6px 16px rgba(108,99,255,0.3)' : '0 0 0 5px #f5f3ff',
                        animation: (growing && !isMobile) ? 'dotPulse 1.1s ease infinite' : 'none',
                        transition: 'background 0.4s, box-shadow 0.4s',
                      }}>
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>

                    {/* カード */}
                    <div ref={(el) => { cardRefs.current[i] = el; }} className="lb-uc-card lb-feature-card" style={{
                      gridColumn: lane.col,
                      '--lanePulse': lane.color + '99',
                      transform:  'none',
                      background: '#fff', borderRadius: 14, padding: '10px 18px',
                      border: `1.5px solid ${active ? lane.color + '66' : '#ede9fe'}`,
                      boxShadow: active ? `0 8px 24px ${lane.color}1f` : '0 2px 10px rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', gap: 14,
                      animation: isMobile ? 'none'
                        : growing ? 'cardBorderPulse 0.9s ease-in-out infinite'
                        : settled ? 'cardPop 0.5s ease'
                        : 'none',
                      transition: 'border-color 0.4s, box-shadow 0.4s, transform 0.4s ease',
                    }}>
                      <span style={{ fontSize: 26, flexShrink: 0 }}>{step.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          {/* モバイル用アクターチップ */}
                          <span className="lb-uc-actor-chip" style={{
                            display: 'none', alignItems: 'center', fontSize: 10, fontWeight: 800,
                            color: lane.color, background: `${lane.color}18`, padding: '2px 8px', borderRadius: 20,
                          }}>{lane.label}</span>
                          {step.phase && (
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg,#6c63ff,#8b5cf6)', padding: '2px 9px', borderRadius: 20, letterSpacing: 0.5 }}>
                              {step.phase}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', lineHeight: 1.4 }}>{step.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.6 }}>{step.note}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* スクロール促し（デスクトップ・未完了時）: 中央寄せの高さに影響しないよう絶対配置 */}
            {!isMobile && progress < 0.95 && (
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: -2, textAlign: 'center', fontSize: 12, color: '#a5b4fc', fontWeight: 600, animation: 'fadeIn 0.3s' }}>
                ↓ スクロールで流れが進みます
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const uc = {
  section: (isMobile) => ({
    background: 'linear-gradient(160deg, #f5f3ff 0%, #eff6ff 55%, #fdf2f8 100%)',
    padding: isMobile ? '56px 20px' : 0,
  }),
  scrollWrap: (isMobile, n) => isMobile
    ? {}
    : { height: `${n * 32 + 80}vh`, position: 'relative' },
  sticky: (isMobile) => isMobile
    ? {}
    : { position: 'sticky', top: NAV_H, height: `calc(100vh - ${NAV_H}px)`, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8px 24px' },
};

// ── Landing Page ─────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const goToAccount = () => {
    if (user) {
      navigate('/account');
    } else {
      navigate('/login', { state: { redirect: '/account' } });
    }
  };

  const [featRef, featInView]       = useInView();
  const [demoRef, demoInView]       = useInView();
  const [pricingRef, pricingInView] = useInView();
  const [ctaRef, ctaInView]         = useInView();

  const features = [
    { icon: '🎭', title: 'キャスト・スタッフ管理', desc: 'VRChat名で識別するキャスト登録（重複不可）。スタッフを招待して複数人での運営を分担できます。' },
    { icon: '📋', title: '応募フォーム',            desc: '公開URLを共有するだけでゲストが応募可能。アカウント登録と連携すれば、次回以降は情報が自動入力されます。' },
    { icon: '🎲', title: '4種類の抽選モード',       desc: '「キャスト選択」「ゲスト選択」「両想いマッチング」「ランダム抽選」の4モード。形式に合わせて選択。' },
    { icon: '📅', title: '日程管理・出欠リンク',     desc: '開催日をカレンダーで設定し、キャスト向けの出欠入力リンクを発行。スマホから簡単に出欠回答できます。' },
    { icon: '🏆', title: '自動部屋割り作成',         desc: '抽選結果をもとにルーム割り当てを自動生成。手作業での転記・整理が一切不要になります。' },
    { icon: '🏠', title: 'ゲストポータル',           desc: '応募したゲストが自分の当選・落選・抽選状況をポータルでいつでも確認できます。X URLやDiscordもプロフィールに登録可能。' },
  ];

  const plans = [
    {
      id: 'trial', name: '体験プラン', price: '1ヶ月 無料', sub: 'まずは全機能を試したい方へ',
      badge: null, highlight: false, accent: '#10b981',
      items: [
        '**全機能にアクセス可能**',
        '**1ヶ月間** 無料でお試し',
        'イベント作成：1件',
        'スタッフ招待：1名',
        'Google Formで申請するだけ',
      ],
      cta: '無料体験を申し込む',
      ctaStyle: { background: 'rgba(16,185,129,0.1)', color: '#059669' },
      action: () => window.open(TRIAL_FORM_URL, '_blank'),
    },
    {
      id: 'basic', name: 'Basic', price: `${BASIC_PRICE} / 月`, sub: '小規模なイベント運営に',
      badge: null, highlight: false, accent: '#6c63ff',
      items: [
        'イベント作成：2件まで',
        'スタッフ招待：2名まで',
        'キャスト / ゲスト / ランダム / 両想い抽選',
        '自動部屋割り・日程管理',
        '優先度設定',
      ],
      cta: '購入する',
      ctaStyle: { background: '#f1f5f9', color: '#475569' },
      action: () => goToAccount(),
    },
    {
      id: 'pro', name: 'Pro', price: `${PRO_PRICE} / 月`, sub: '本格的に運営したい方へ',
      badge: 'おすすめ', highlight: true, accent: '#6c63ff',
      items: [
        'イベント作成：**無制限**',
        'スタッフ招待：**無制限**',
        'キャスト / ゲスト / ランダム / 両想い抽選',
        '自動部屋割り・日程管理',
        '優先度設定',
        '**ゲスト参加回数・履歴表示** ★',
        '**インスタンス内のゲストを自動登録** ★',
      ],
      cta: '購入する',
      ctaStyle: { background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', boxShadow: '0 6px 20px rgba(108,99,255,0.38)' },
      action: () => goToAccount(),
    },
  ];

  return (
    <>
      <GlobalStyles />
      <div style={s.root}>

        {/* ── Fixed Nav ── */}
        <nav style={s.nav}>
          <div style={s.navInner}>
            <div style={s.navBrand}>
              <img src="/logo.png" alt="LinkBoard" style={s.navLogo} />
              <span style={s.navBrandText}>LinkBoard</span>
            </div>
            <div className="lb-nav-links">
              <a href="#flow"     className="lb-nav-link" style={s.navLink}>使い方</a>
              <a href="#features" className="lb-nav-link" style={s.navLink}>機能</a>
              <a href="#demo"     className="lb-nav-link" style={s.navLink}>デモ</a>
              <a href="#pricing"  className="lb-nav-link" style={s.navLink}>料金</a>
              <a href={CONTACT_URL} target="_blank" rel="noreferrer" className="lb-nav-link" style={s.navLink}>お問い合わせ</a>
              <button className="lb-primary-btn" onClick={() => navigate('/login')} style={s.navCta}>ログイン</button>
            </div>
            {/* モバイル用: ログインボタンのみ */}
            <button className="lb-primary-btn lb-nav-mobile-cta" onClick={() => navigate('/login')} style={{ ...s.navCta, display: 'none' }}>ログイン</button>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="lb-hero" style={s.hero}>
          {/* BG decoration */}
          <div style={s.heroBgBlob1} />
          <div style={s.heroBgBlob2} />

          <div style={s.heroLeft}>
            <div style={s.heroBadge}>
              <img src="/logo.png" alt="" style={{ height: 16, width: 'auto' }} />
              VRChatイベント管理ツール
            </div>
            <h1 className="lb-hero-title-size" style={s.heroTitle}>
              VRChatイベントを<br />
              <span className="lb-hero-title-accent">もっとスマートに。</span>
            </h1>
            <p className="lb-hero-desc-size" style={s.heroDesc}>
              運営側は応募管理・抽選・部屋割りをワンストップで。<br />
              ゲスト側は応募フォームから当選確認まで、すべてここで完結。
            </p>
            <div style={s.heroCtas}>
              <button
                className="lb-primary-btn"
                onClick={() => window.open(TRIAL_FORM_URL, '_blank')}
                style={s.heroPrimary}
              >
                無料体験を申し込む →
              </button>
              <a href="#demo" className="lb-secondary-btn" style={s.heroSecondary}>デモを見る</a>
            </div>
            <div style={s.heroStats}>
              {[
                { num: '4',  label: '抽選モード' },
                { num: '公開', label: '応募フォーム' },
                { num: '∞',  label: 'Proはイベント無制限' },
              ].map((st, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={s.statDiv} />}
                  <div style={s.stat}>
                    <span style={s.statNum}>{st.num}</span>
                    <span style={s.statLabel}>{st.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Hero card */}
          <div className="lb-hero-card lb-hero-right" style={s.heroRight}>
            <div style={s.heroCard}>
              <div style={s.heroCardBar}>
                <div style={s.dot('#ef4444')} />
                <div style={s.dot('#f59e0b')} />
                <div style={s.dot('#10b981')} />
                <span style={s.heroCardBarTitle}>マッチング結果</span>
                <span style={s.heroCardDone}>完了</span>
              </div>
              <div style={s.heroCardBody}>
                {[
                  { cast: 'キャストA', guest: 'ゲスト1', room: 'Room A', heart: true },
                  { cast: 'キャストB', guest: 'ゲスト3', room: 'Room B', heart: true },
                  { cast: 'キャストC', guest: 'ゲスト2', room: 'Room C', heart: false },
                ].map((r, i) => (
                  <div key={i} style={{ ...s.miniRow, animationDelay: `${i * 0.15}s` }}>
                    <div style={{ ...s.miniAv, background: CAST_COLORS[i] }}>{r.cast[3]}</div>
                    <div style={s.miniName}>{r.cast}</div>
                    <div style={{ fontSize: 13, flexShrink: 0 }}>{r.heart ? '❤️' : '→'}</div>
                    <div style={s.miniName}>{r.guest}</div>
                    <div style={s.miniRoom}>{r.room}</div>
                  </div>
                ))}
                <div style={s.heroAutoNote}>部屋割り自動生成済み ✓</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Flow (動くユースケース図) ── */}
        <UseCaseFlow />

        {/* ── Features ── */}
        <section id="features" className="lb-section-pad" style={s.section}>
          <div ref={featRef} style={s.sectionInner}>
            <div style={{ ...s.sectionTag, ...(featInView ? s.animIn : s.animOut) }}>FEATURES</div>
            <h2 style={{ ...s.sectionTitle, ...(featInView ? { ...s.animIn, transitionDelay: '0.1s' } : s.animOut) }}>
              イベント運営に必要な機能がすべて揃う
            </h2>
            <p style={{ ...s.sectionDesc, ...(featInView ? { ...s.animIn, transitionDelay: '0.18s' } : s.animOut) }}>
              イベントの準備から当日運営・参加者への結果通知まで、ワンストップでサポート
            </p>
            <div className="lb-feature-grid" style={s.featureGrid}>
              {features.map((f, i) => (
                <div
                  key={i}
                  className="lb-feature-card"
                  style={{
                    ...s.featureCard,
                    ...(featInView ? { ...s.animIn, transitionDelay: `${0.1 + i * 0.08}s` } : s.animOut),
                  }}
                >
                  <div style={s.featureIconWrap}>{f.icon}</div>
                  <h3 style={s.featureTitle}>{f.title}</h3>
                  <p style={s.featureDesc}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Demo ── */}
        <section id="demo" className="lb-section-pad" style={s.demoSection}>
          {/* decorative circles */}
          <div style={s.demoBlobL} />
          <div style={s.demoBlobR} />
          <div ref={demoRef} style={s.sectionInner}>
            <div style={{ ...s.sectionTag, color: '#818cf8', ...(demoInView ? s.animIn : s.animOut) }}>LIVE DEMO</div>
            <h2 style={{ ...s.sectionTitle, color: '#f1f5f9', ...(demoInView ? { ...s.animIn, transitionDelay: '0.1s' } : s.animOut) }}>
              実際に触って体験してみよう
            </h2>
            <p style={{ ...s.sectionDesc, color: '#64748b', ...(demoInView ? { ...s.animIn, transitionDelay: '0.18s' } : s.animOut) }}>
              キャスト・ゲストの追加から抽選・部屋割りまで、すべて試せます
            </p>
            <div style={{ ...(demoInView ? { ...s.animIn, transitionDelay: '0.25s' } : s.animOut) }}>
              <DemoPreview />
            </div>
            <p style={{ textAlign: 'center', marginTop: 18, color: '#475569', fontSize: 13, ...(demoInView ? { ...s.animIn, transitionDelay: '0.35s' } : s.animOut) }}>
              ※ キャストやゲストを追加して「🎲 抽選実行」タブからマッチングを試してみてください
            </p>
          </div>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" style={s.section}>
          <div ref={pricingRef} style={s.sectionInner}>
            <div style={{ ...s.sectionTag, ...(pricingInView ? s.animIn : s.animOut) }}>PRICING</div>
            <h2 style={{ ...s.sectionTitle, ...(pricingInView ? { ...s.animIn, transitionDelay: '0.1s' } : s.animOut) }}>料金プラン</h2>
            <p style={{ ...s.sectionDesc, ...(pricingInView ? { ...s.animIn, transitionDelay: '0.18s' } : s.animOut) }}>
              まずは無料体験から。本格運営はBoothで購入。
            </p>
            <div className="lb-plan-grid" style={s.planGrid}>
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className="lb-plan-card"
                  style={{
                    ...s.planCard,
                    border: plan.highlight ? '2px solid #6c63ff' : '2px solid #e2e8f0',
                    boxShadow: plan.highlight ? '0 4px 24px rgba(108,99,255,0.18)' : '0 1px 6px rgba(0,0,0,0.06)',
                    ...(pricingInView ? { ...s.animIn, transitionDelay: `${0.15 + i * 0.1}s` } : s.animOut),
                  }}
                >
                  {plan.badge && <div style={s.planBadge}>{plan.badge}</div>}
                  <div style={{ ...s.planAccentBar, background: plan.accent }} />
                  <div style={s.planName}>{plan.name}</div>
                  <div style={s.planSub}>{plan.sub}</div>
                  <ul style={s.planItems}>
                    {plan.items.map((item, j) => (
                      <li key={j} style={s.planItem}>
                        <span style={{ ...s.planCheck, color: plan.accent }}>✓</span>
                        <span>{renderBold(item)}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.price && <div style={s.planPrice}>{plan.price}</div>}
                  <button
                    className="lb-primary-btn"
                    onClick={plan.action}
                    style={{ ...s.planCta, ...plan.ctaStyle }}
                  >
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>
            {/* Proカードの下に注記（グリッドと同列で3列目に配置） */}
            <div className="lb-plan-grid" style={{ ...s.planGrid, marginTop: 8 }}>
              <div />
              <div />
              <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>★ Proプラン限定機能</p>
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="lb-cta-banner" style={s.ctaBanner}>
          <div ref={ctaRef} style={s.ctaInner}>
            <div style={{ ...(ctaInView ? { ...s.animIn } : s.animOut) }}>
              <h2 style={s.ctaTitle}>まずは無料体験から始めよう</h2>
              <p style={s.ctaDesc}>Google Formで申請するだけ。すぐに体験できます。</p>
              <div className="lb-cta-btns" style={s.ctaBtns}>
                <button
                  className="lb-primary-btn"
                  onClick={() => window.open(TRIAL_FORM_URL, '_blank')}
                  style={s.ctaPrimary}
                >
                  無料体験を申し込む
                </button>
                <a href={BOOTH_URL} target="_blank" rel="noreferrer" className="lb-secondary-btn" style={s.ctaSecondary}>
                  Boothで購入する
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={s.footer}>
          <div style={s.footerInner}>
            <div style={s.footerBrand}>
              <img src="/logo.png" alt="LinkBoard" style={{ height: 28, width: 'auto' }} />
              <span style={s.footerBrandText}>LinkBoard</span>
            </div>
            <div style={s.footerLinks}>
              {[
                { label: '機能',   href: '#features' },
                { label: 'デモ',   href: '#demo' },
                { label: '料金',   href: '#pricing' },
                { label: 'Booth',  href: BOOTH_URL, external: true },
                { label: 'お問い合わせ (X)', href: CONTACT_URL, external: true },
              ].map(l => (
                <a key={l.label} href={l.href} target={l.external ? '_blank' : undefined} rel={l.external ? 'noreferrer' : undefined} className="lb-footer-link" style={s.footerLink}>{l.label}</a>
              ))}
            </div>
            <div style={s.footerCopy}>© 2026 LinkBoard — VRChat イベント管理プラットフォーム</div>
          </div>
        </footer>

      </div>
    </>
  );
};

const NAV_H = 60;

const s = {
  root: { fontFamily: "'Inter','Helvetica Neue','Hiragino Sans','Yu Gothic',sans-serif", background: '#fff', color: '#1e293b', overflowX: 'clip', paddingTop: NAV_H },

  // animation helpers
  animIn:  { opacity: 1, transform: 'translateY(0)',   transition: 'opacity 0.6s ease, transform 0.6s ease' },
  animOut: { opacity: 0, transform: 'translateY(28px)' },

  // Nav
  nav: { position: 'fixed', top: 0, left: 0, right: 0, height: NAV_H, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(0,0,0,0.06)', zIndex: 200, display: 'flex', alignItems: 'center' },
  navInner: { maxWidth: 1100, margin: '0 auto', padding: '0 24px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { display: 'flex', alignItems: 'center', gap: 8 },
  navLogo:  { height: 32, width: 'auto' },
  navBrandText: { fontSize: 17, fontWeight: 800, color: '#0f172a' },
  navLinks: { display: 'flex', alignItems: 'center', gap: 28 },
  navLink:  { fontSize: 14, fontWeight: 500, color: '#64748b', textDecoration: 'none' },
  navCta:   { background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 10px rgba(108,99,255,0.3)' },

  // Hero
  hero: { maxWidth: 1100, margin: '0 auto', position: 'relative' },
  heroBgBlob1: { position: 'absolute', top: 0, right: -100, width: 600, height: 600, background: 'radial-gradient(circle, rgba(108,99,255,0.07) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  heroBgBlob2: { position: 'absolute', bottom: -80, left: -80, width: 400, height: 400, background: 'radial-gradient(circle, rgba(236,72,153,0.05) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  heroLeft: { flex: 1, position: 'relative', zIndex: 1 },
  heroBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#6c63ff', background: 'rgba(108,99,255,0.08)', padding: '6px 14px', borderRadius: 20, marginBottom: 24, border: '1px solid rgba(108,99,255,0.15)', animation: 'badge-pop 0.5s ease both' },
  heroTitle: { fontSize: 48, fontWeight: 900, lineHeight: 1.2, margin: '0 0 20px', color: '#0f172a', animation: 'fadeUp 0.65s ease 0.1s both' },
  heroDesc:  { fontSize: 17, color: '#64748b', lineHeight: 1.8, margin: '0 0 32px', animation: 'fadeUp 0.65s ease 0.2s both' },
  heroCtas:  { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 40, flexWrap: 'wrap', animation: 'fadeUp 0.65s ease 0.3s both' },
  heroPrimary: { background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 28px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 24px rgba(108,99,255,0.4)' },
  heroSecondary: { color: '#6c63ff', fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '14px 4px', borderBottom: '2px solid rgba(108,99,255,0.25)' },
  heroStats: { display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', animation: 'fadeUp 0.65s ease 0.4s both' },
  stat:      { display: 'flex', flexDirection: 'column', gap: 2 },
  statNum:   { fontSize: 22, fontWeight: 900, color: '#6c63ff' },
  statLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 500 },
  statDiv:   { width: 1, height: 36, background: '#e2e8f0' },

  heroRight: { flexShrink: 0, position: 'relative', zIndex: 1 },
  heroCard:  { background: '#0f172a', borderRadius: 18, overflow: 'hidden', width: 300, boxShadow: '0 28px 72px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.05)', animation: 'glowPulse 3s ease-in-out infinite' },
  heroCardBar: { background: '#1e293b', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6 },
  heroCardBarTitle: { flex: 1, fontSize: 11, fontWeight: 700, color: '#94a3b8', marginLeft: 4 },
  heroCardDone:     { fontSize: 9, background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '2px 8px', borderRadius: 20, fontWeight: 700 },
  dot: (c) => ({ width: 10, height: 10, borderRadius: '50%', background: c, flexShrink: 0 }),
  heroCardBody: { padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  miniRow: { display: 'flex', alignItems: 'center', gap: 7, background: '#1e293b', padding: '8px 10px', borderRadius: 9, animation: 'fadeUp 0.4s ease both' },
  miniAv:   (bg) => ({ width: 22, height: 22, borderRadius: 6, background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }),
  miniName: { fontSize: 11, color: '#e2e8f0', fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  miniRoom: { fontSize: 9.5, color: '#818cf8', background: 'rgba(129,140,248,0.15)', padding: '2px 6px', borderRadius: 10, fontWeight: 700, whiteSpace: 'nowrap' },
  heroAutoNote: { fontSize: 10.5, color: '#10b981', textAlign: 'center', fontWeight: 700, paddingTop: 4 },

  // Sections
  section:      { padding: '88px 24px', background: '#fff' },
  demoSection:  { padding: '88px 24px', background: '#0a0f1e', position: 'relative', overflow: 'hidden' },
  demoBlobL:    { position: 'absolute', top: '20%', left: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(108,99,255,0.12) 0%, transparent 70%)', pointerEvents: 'none' },
  demoBlobR:    { position: 'absolute', bottom: '10%', right: -60, width: 360, height: 360, background: 'radial-gradient(circle, rgba(236,72,153,0.08) 0%, transparent 70%)', pointerEvents: 'none' },
  sectionInner: { maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  sectionTag:   { fontSize: 12, fontWeight: 700, color: '#6c63ff', letterSpacing: 2, marginBottom: 12 },
  sectionTitle: { fontSize: 34, fontWeight: 900, color: '#0f172a', margin: '0 0 12px' },
  sectionDesc:  { fontSize: 17, color: '#64748b', margin: '0 0 48px' },

  // Features
  featureGrid: { gap: 24 },
  featureCard: { background: '#fafbff', border: '1.5px solid #f1f5f9', borderRadius: 18, padding: '28px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  featureIconWrap: { fontSize: 30, marginBottom: 14, display: 'inline-block' },
  featureTitle: { fontSize: 15.5, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' },
  featureDesc:  { fontSize: 14, color: '#64748b', lineHeight: 1.8, margin: 0 },

  // Pricing
  planGrid: { gap: 24 },
  planCard:  { borderRadius: 20, padding: '28px 24px 24px', position: 'relative', background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  planBadge: { position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 16px', borderRadius: '0 0 10px 10px', whiteSpace: 'nowrap' },
  planAccentBar: { height: 3, borderRadius: 3, marginBottom: 20, width: 40 },
  planName:  { fontSize: 21, fontWeight: 900, color: '#1e293b', marginBottom: 4 },
  planPrice: { fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 4 },
  planSub:   { fontSize: 13, color: '#94a3b8', marginBottom: 22 },
  planItems: { listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 },
  planItem:  { fontSize: 13.5, color: '#475569', display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5 },
  planCheck: { fontWeight: 800, flexShrink: 0, marginTop: 1 },
  planCta:   { width: '100%', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },

  // CTA Banner
  ctaBanner: { padding: '88px 24px' },
  ctaInner:  { maxWidth: 560, margin: '0 auto', textAlign: 'center' },
  ctaLogoImg: { height: 56, width: 'auto', marginBottom: 20, filter: 'brightness(0) invert(1) opacity(0.85)' },
  ctaTitle:  { fontSize: 34, fontWeight: 900, color: '#f1f5f9', margin: '0 0 12px' },
  ctaDesc:   { fontSize: 17, color: '#64748b', margin: '0 0 36px' },
  ctaBtns:   { display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' },
  ctaPrimary: { background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 28px rgba(108,99,255,0.44)' },
  ctaSecondary: { background: 'rgba(255,255,255,0.07)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '14px 32px', fontSize: 16, fontWeight: 700, textDecoration: 'none', display: 'inline-block' },

  // Footer
  footer: { background: '#0c1424', padding: '48px 24px 32px' },
  footerInner: { maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 },
  footerBrand: { display: 'flex', alignItems: 'center', gap: 8 },
  footerBrandText: { fontSize: 16, fontWeight: 800, color: '#e2e8f0' },
  footerLinks: { display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' },
  footerLink:  { fontSize: 13, color: '#475569', textDecoration: 'none' },
  footerCopy:  { fontSize: 12, color: '#334155' },
};

export default LandingPage;
